import { Button, radius } from '@alison/ui';
import { useTenant } from '@alison/whitelabel/react';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import TuneIcon from '@mui/icons-material/Tune';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { GLYPH_SX, MetaRow, StatusLabel, statusChipSx } from './channelCardShared.js';
import type { SlackData } from './notifications.types.js';
import { OverrideChip } from './OverrideChip.js';
import { SlackIcon } from './SlackIcon.js';

export function SlackChannelCard({
  data,
  canManage,
  testPending,
  onConnect,
  onReconnect,
  onConfigure,
  onRefresh,
  onManage,
  onTest,
}: {
  data: SlackData;
  canManage: boolean;
  testPending: boolean;
  onConnect: () => void;
  onReconnect: () => void;
  onConfigure: () => void;
  onRefresh: () => void;
  onManage: () => void;
  onTest: () => void;
}): ReactNode {
  const { providerName } = useTenant();
  const connected = data.status === 'connected';
  const needsAttention = data.status === 'needs-attention';
  const unavailable = data.availability === 'unavailable';
  const needsSetup = connected && data.channel.trim() === '';
  const ready = connected && !unavailable && !needsSetup;
  const displayStatus = unavailable ? 'unavailable' : needsSetup ? 'setup-incomplete' : data.status;
  const warning = needsAttention || unavailable || needsSetup;

  return (
    <Stack
      sx={{
        flex: 1,
        bgcolor: connected || needsAttention ? 'background.paper' : 'background.page',
        border: '1px solid',
        borderColor: warning ? 'warning.main' : 'divider',
        borderRadius: `${radius.xl}px`,
        boxShadow: (theme) =>
          warning
            ? `0 0 0 1px ${theme.palette.warning.main}, ${theme.shadows[5]}`
            : theme.shadows[5],
        p: 4,
        gap: 3,
        minHeight: 258,
      }}
    >
      <Stack direction="row" sx={{ gap: 3, alignItems: 'flex-start' }}>
        <Box sx={{ ...GLYPH_SX, bgcolor: (theme) => theme.palette.network.slack }}>
          <SlackIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'text.primary' }}>
              Slack
            </Typography>
            <Chip
              size="small"
              label={<StatusLabel status={displayStatus} />}
              sx={statusChipSx(displayStatus)}
            />
          </Stack>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>
            Post notifications into your workspace channels.
          </Typography>
        </Box>
      </Stack>

      {needsAttention && !unavailable && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
            py: 2,
            px: 3,
            bgcolor: 'warning.bg',
            borderRadius: `${radius.lg}px`,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 14, color: 'warning.dark', flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'warning.dark' }}>
            Slack token expired. Messages aren't being delivered.
          </Typography>
        </Stack>
      )}

      {unavailable && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
            py: 2,
            px: 3,
            bgcolor: 'warning.bg',
            borderRadius: `${radius.lg}px`,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 14, color: 'warning.dark', flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'warning.dark' }}>
            Slack status could not be verified. Delivery controls are paused.
          </Typography>
        </Stack>
      )}

      {needsSetup && !unavailable && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
            py: 2,
            px: 3,
            bgcolor: 'warning.bg',
            borderRadius: `${radius.lg}px`,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 14, color: 'warning.dark', flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'warning.dark' }}>
            Choose a default channel before Slack delivery can start.
          </Typography>
        </Stack>
      )}

      {connected && !unavailable && (
        <Stack sx={{ gap: 1, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
          <MetaRow label="Workspace">{data.workspace}</MetaRow>
          {data.channel !== '' && (
            <MetaRow label="Default channel">
              <Typography component="span" sx={{ color: 'text.secondary' }}>
                #
              </Typography>
              {data.channel}
            </MetaRow>
          )}
          {data.overrides.length > 0 && (
            <MetaRow label="Overrides">
              {data.overrides.map((override) => (
                <OverrideChip
                  key={override.event}
                  label={`${override.event.split(' ')[0]} → #${override.channel}`}
                />
              ))}
            </MetaRow>
          )}
        </Stack>
      )}

      {!connected && !needsAttention && (
        <Typography
          sx={{
            fontSize: 12,
            color: 'text.secondary',
            pt: 3,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          {canManage
            ? `Connect your Slack workspace so ${providerName} can post into the channels your team already watches.`
            : 'Ask a workspace admin to connect Slack.'}
        </Typography>
      )}

      <Stack direction="row" sx={{ gap: 1, mt: 'auto' }}>
        {needsAttention && !unavailable && canManage && (
          <Button
            size="small"
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={onReconnect}
          >
            Reconnect
          </Button>
        )}
        {unavailable && (
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={onRefresh}>
            Retry status
          </Button>
        )}
        {needsSetup && !unavailable && canManage && (
          <Button size="small" variant="gradient" onClick={onConfigure}>
            Finish setup
          </Button>
        )}
        {ready && (
          <Button
            size="small"
            variant="text"
            startIcon={<SendIcon />}
            onClick={onTest}
            disabled={testPending}
            sx={{ color: 'text.secondary' }}
          >
            Send test
          </Button>
        )}
        {ready && canManage && (
          <Button size="small" variant="outlined" startIcon={<TuneIcon />} onClick={onManage}>
            Manage
          </Button>
        )}
        {!connected && !needsAttention && canManage && (
          <Button size="small" variant="gradient" startIcon={<LinkIcon />} onClick={onConnect}>
            Connect
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
